import {
  ClockCounterClockwise,
  EyeSlash,
  Ghost,
  Lightning,
  Link,
  Tray,
  TrendDown,
} from "@phosphor-icons/react"
import { createFileRoute, Outlet, useLocation } from "@tanstack/react-router"
import { useMemo, useState } from "react"
import ActivateVariantDialog from "~/components/npi/ActivateVariantDialog/ActivateVariantDialog"
import SeeLinkedVariantDialog from "~/components/npi/SeeLinkedVariantDialog"
import { VariantStatusDrawer } from "~/components/npi/VariantStatusDrawer"
import { BreadcrumbWrapper } from "~/components/reusable/Breadcrumb"
import { Module } from "~/components/design-system/pages"
import { type HeaderTab } from "~/components/reusable/HeaderTabs"
import { ProductTableProvider } from "~/context/ProductTableProvider"
import { usePageTracking } from "~/hooks/usePageTracking"
import { useLinkedVariants } from "~/hooks/useVariants"
import { type IVariantSummary } from "~/hooks/useVariantTableData"
import { useUser } from "~/providers/userProvider"
import { type INpiVariantSummary } from "~/server/api/routers/npi"
import { api } from "~/utils/api"
import useShowInternalFeatures from "~/hooks/useShowInternalFeatures"
import type { NpiTabContext } from "~/constants/productTable"

export const Route = createFileRoute("/_auth/product-management/_layout")({
  component: RouteComponent,
})

enum PMTab {
  active = "Active",
  linkedVariants = "Linked Variants",
  inbox = "To review",
  phasingOut = "Phasing Out",
  phasedOut = "Phased Out",
  hidden = "Hidden",
  // Hakio-only debug tab
  ghostTown = "Ghost Variants",
}

type PMHeaderTab = HeaderTab

const allTabs: PMHeaderTab[] = [
  {
    label: PMTab.inbox,
    path: "/product-management/inbox",
    icon: <Tray />,
    tooltip: "Variants needing action: add to collection, hide, or phase out",
  },
  {
    label: PMTab.active,
    path: "/product-management/active",
    icon: <Lightning />,
    tooltip: "Variants in collections with active forecasting",
  },
  {
    label: PMTab.phasingOut,
    path: "/product-management/phasing-out",
    icon: <TrendDown />,
  },
  {
    label: PMTab.phasedOut,
    path: "/product-management/phased-out",
    icon: <ClockCounterClockwise />,
    tooltip: "Historical variants, still visible in reports",
  },
  {
    label: PMTab.hidden,
    path: "/product-management/hidden",
    icon: <EyeSlash />,
    tooltip: "Variants excluded from all reports",
  },
  {
    label: PMTab.linkedVariants,
    path: "/product-management/linked-variants",
    icon: <Link />,
  },
  // Hakio-only debug tab
  {
    label: PMTab.ghostTown,
    path: "/product-management/ghost-variants",
    icon: <Ghost />,
    tooltip: "Variants that fit no other category. No forecast, no action available",
    hakioOnly: true,
  },
]

function RouteComponent() {
  const { permissions } = useUser()
  const [showInternalFeatures] = useShowInternalFeatures()
  const linkedVariants = useLinkedVariants()
  const { data: categories } = api.productManagement.categorizeVariants.useQuery()

  const [openLinked, setOpenLinked] = useState(false)
  const [openActivate, setOpenActivate] = useState(false)
  const [openStatusDrawer, setOpenStatusDrawer] = useState(false)
  const location = useLocation()

  const countByTab: Partial<Record<PMTab, number | undefined>> = useMemo(
    () => ({
      [PMTab.inbox]: categories?.inbox.length,
      [PMTab.active]: categories?.active.length,
      [PMTab.phasingOut]: categories?.phasingOut.length,
      [PMTab.phasedOut]: categories?.phasedOut.length,
      [PMTab.hidden]: categories?.hidden.length,
      [PMTab.ghostTown]: categories?.ghostTown.length,
    }),
    [categories],
  )

  const tabs: HeaderTab[] = useMemo(() => {
    return allTabs
      .filter((tab) => {
        if (tab.hakioOnly && !(permissions.isStaff && showInternalFeatures)) return false
        return true
      })
      .map((tab) => ({
        ...tab,
        count: countByTab[tab.label as PMTab],
      }))
  }, [permissions.isStaff, showInternalFeatures, countByTab])

  const currentTab = useMemo(
    () => tabs.find((x) => x.path === location.pathname)?.label ?? "",
    [tabs, location.pathname],
  )
  const [selectedVariants, setSelectedVariants] = useState<
    IVariantSummary[] | INpiVariantSummary[]
  >([])

  const handleRowClicked = (variants: IVariantSummary[] | INpiVariantSummary[]) => {
    setSelectedVariants(variants)

    // Open status drawer for inbox, hidden, phasing out, and phased out tabs
    if (
      currentTab === PMTab.inbox ||
      currentTab === PMTab.hidden ||
      currentTab === PMTab.phasingOut ||
      currentTab === PMTab.phasedOut
    ) {
      setOpenStatusDrawer(true)
      return
    }

    // Open linked variant dialog for linked variants tab
    if (currentTab === PMTab.linkedVariants) {
      setOpenLinked(true)
      return
    }

    // For active tab, open status drawer (allow quick actions)
    if (currentTab === PMTab.active) {
      setOpenStatusDrawer(true)
      return
    }

    // Default: open linked dialog
    setOpenLinked(true)
  }

  const drawerTabContext = useMemo<NpiTabContext>(() => {
    if (currentTab === PMTab.active) return "active"
    if (currentTab === PMTab.hidden) return "hidden"
    if (currentTab === PMTab.phasingOut) return "phasingOut"
    if (currentTab === PMTab.phasedOut) return "phasedOut"
    return "inbox"
  }, [currentTab])

  usePageTracking("Product Management")

  return (
    <Module breadcrumb={<BreadcrumbWrapper />} tabs={{ items: tabs, path: "/product-management" }}>
      <ProductTableProvider value={{ onRowClicked: handleRowClicked }}>
        <Outlet />
      </ProductTableProvider>

      <SeeLinkedVariantDialog
        variants={selectedVariants}
        open={openLinked}
        setOpen={setOpenLinked}
      />

      <ActivateVariantDialog
        linkedVariantIds={linkedVariants.map((v) => v.variantId)}
        variants={selectedVariants}
        open={openActivate}
        setOpen={setOpenActivate}
      />

      <VariantStatusDrawer
        open={openStatusDrawer}
        onOpenChange={setOpenStatusDrawer}
        variant={selectedVariants[0] ?? null}
        tabContext={drawerTabContext}
      />
    </Module>
  )
}
